/**
 * Money helpers. Hard rules (docs/08-payments-billing.md):
 *   - integer minor units everywhere, never floats
 *   - commission rounding is half-up, computed once and snapshotted
 */
/** Largest amount we accept, to keep intermediate products inside Number.MAX_SAFE_INTEGER. */
const MAX_AMOUNT_CENTS = 1_000_000_000_000; // 10 billion major units
const BPS_DIVISOR = 10_000;
function assertMinorUnits(value, label) {
    if (!Number.isInteger(value)) {
        throw new TypeError(`${label} must be an integer number of minor units, got ${value}`);
    }
    if (value < 0)
        throw new RangeError(`${label} must not be negative, got ${value}`);
    if (value > MAX_AMOUNT_CENTS)
        throw new RangeError(`${label} exceeds the maximum supported amount`);
}
/** Integer division rounding half away from zero (inputs here are non-negative). */
function divideRoundHalfUp(numerator, denominator) {
    if (denominator <= 0)
        throw new RangeError('denominator must be positive');
    return Math.floor((numerator + denominator / 2) / denominator);
}
/**
 * Platform commission for a booking total, in minor units.
 * @param totalCents booking total in minor units
 * @param bps basis points from the active contract (500 = 5%)
 */
export function commissionCents(totalCents, bps) {
    assertMinorUnits(totalCents, 'totalCents');
    if (!Number.isInteger(bps))
        throw new TypeError(`bps must be an integer, got ${bps}`);
    if (bps < 0 || bps > BPS_DIVISOR)
        throw new RangeError(`bps must be between 0 and ${BPS_DIVISOR}`);
    return divideRoundHalfUp(totalCents * bps, BPS_DIVISOR);
}
/** Sum minor-unit amounts, rejecting anything non-integer. */
export function sumCents(amounts) {
    let total = 0;
    for (const amount of amounts) {
        if (!Number.isInteger(amount)) {
            throw new TypeError(`amount must be an integer number of minor units, got ${amount}`);
        }
        total += amount;
    }
    return total;
}
function daysInUtcMonth(year, monthIndex) {
    return new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
}
/**
 * Monthly platform fee prorated by the days covered inside a single calendar month.
 * `periodStart`/`periodEnd` are inclusive and must fall in the same UTC month.
 */
export function prorateMonthlyFee(monthlyFeeCents, periodStart, periodEnd) {
    assertMinorUnits(monthlyFeeCents, 'monthlyFeeCents');
    if (periodEnd.getTime() < periodStart.getTime()) {
        throw new RangeError('periodEnd must not be before periodStart');
    }
    if (periodStart.getUTCFullYear() !== periodEnd.getUTCFullYear() ||
        periodStart.getUTCMonth() !== periodEnd.getUTCMonth()) {
        throw new RangeError('prorateMonthlyFee expects a period inside one calendar month');
    }
    const totalDays = daysInUtcMonth(periodStart.getUTCFullYear(), periodStart.getUTCMonth());
    const coveredDays = periodEnd.getUTCDate() - periodStart.getUTCDate() + 1;
    if (coveredDays >= totalDays)
        return monthlyFeeCents;
    return divideRoundHalfUp(monthlyFeeCents * coveredDays, totalDays);
}
/** Number of decimal places a currency uses, per Intl (JPY = 0, EUR = 2, ...). */
export function currencyDecimals(currency, locale = 'en-US') {
    const options = new Intl.NumberFormat(locale, { style: 'currency', currency }).resolvedOptions();
    return options.maximumFractionDigits ?? 2;
}
/** Render minor units for display. Never use the result for arithmetic. */
export function formatMoney(amountCents, currency, locale = 'en-US') {
    const decimals = currencyDecimals(currency, locale);
    const major = amountCents / 10 ** decimals;
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(major);
}
//# sourceMappingURL=money.js.map